/** XeAgents.ai - Plan Management */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Plus, Loader2, Pencil, Trash2, DollarSign, Users, Phone, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  country: string;
  seatingEnabled: boolean;
  maxSeats: number;
  monthlyPrice: string;
  yearlyPrice: string | null;
  razorpayMonthlyPrice: string | null;
  razorpayYearlyPrice: string | null;
  maxAgents: number;
  maxCampaigns: number;
  maxContactsPerCampaign: number;
  maxWebhooks: number;
  maxKnowledgeBases: number;
  maxFlows: number;
  maxPhoneNumbers: number;
  phoneNumberPurchaseCredits: number;
  includedCredits: number;
  perMinuteRate: string | null;
  canChooseLlm: boolean;
  canPurchaseNumbers: boolean;
  useSystemPool: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PlanFormData {
  name: string;
  displayName: string;
  description: string;
  country: string;
  seatingEnabled: boolean;
  maxSeats: number;
  monthlyPrice: number;
  yearlyPrice: number | null;
  razorpayMonthlyPrice: number | null;
  razorpayYearlyPrice: number | null;
  maxAgents: number;
  maxCampaigns: number;
  maxContactsPerCampaign: number;
  maxWebhooks: number;
  maxKnowledgeBases: number;
  maxFlows: number;
  maxPhoneNumbers: number;
  phoneNumberPurchaseCredits: number;
  includedCredits: number;
  perMinuteRate: number | null;
  canChooseLlm: boolean;
  canPurchaseNumbers: boolean;
  useSystemPool: boolean;
}

export default function PlanManagement() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>({
    name: "",
    displayName: "",
    description: "",
    country: "US",
    seatingEnabled: false,
    maxSeats: 1,
    monthlyPrice: 0,
    yearlyPrice: null,
    razorpayMonthlyPrice: null,
    razorpayYearlyPrice: null,
    maxAgents: 1,
    maxCampaigns: 1,
    maxContactsPerCampaign: 100,
    maxWebhooks: 3,
    maxKnowledgeBases: 5,
    maxFlows: 3,
    maxPhoneNumbers: 0,
    phoneNumberPurchaseCredits: 25,
    includedCredits: 0,
    perMinuteRate: null,
    canChooseLlm: false,
    canPurchaseNumbers: false,
    useSystemPool: false,
  });

  const { data: plans, isLoading } = useQuery<Plan[]>({
    queryKey: ["/api/admin/plans", { app: 'xevoicepro' }],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/plans?app=xevoicepro');
      if (!res.ok) throw new Error('Failed to fetch plans');
      return res.json();
    },
  });

  const [selectedCountry, setSelectedCountry] = useState<string>("US");

  // Filter by country (exclude XeHirePro plans) and sort by price in ascending order
  const filteredPlans = (plans || [])
    .filter((plan) => plan.country === selectedCountry && !plan.name.startsWith('xehirepro_'))
    .sort((a, b) => {
      // For India: use razorpayMonthlyPrice, fallback to monthlyPrice
      // For US: use monthlyPrice
      const isIndia = selectedCountry === "IN";
      const priceA = parseFloat(
        isIndia
          ? (a.razorpayMonthlyPrice ?? a.monthlyPrice ?? '0')
          : (a.monthlyPrice ?? '0')
      );
      const priceB = parseFloat(
        isIndia
          ? (b.razorpayMonthlyPrice ?? b.monthlyPrice ?? '0')
          : (b.monthlyPrice ?? '0')
      );
      return priceA - priceB;
    });

  const createPlan = useMutation({
    mutationFn: async (data: PlanFormData) => {
      return apiRequest("POST", "/api/admin/plans", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Created",
        description: "The subscription plan has been created successfully",
      });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Create Plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePlan = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PlanFormData> }) => {
      return apiRequest("PATCH", `/api/admin/plans/${id}`, { ...data, forceStripeSync: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Updated",
        description: "The subscription plan has been updated successfully",
      });
      setIsEditDialogOpen(false);
      setEditingPlan(null);
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Update Plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deletePlan = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/plans/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plans"] });
      toast({
        title: "Plan Deleted",
        description: "The subscription plan has been deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Delete Plan",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      country: "US",
      seatingEnabled: false,
      maxSeats: 1,
      monthlyPrice: 0,
      yearlyPrice: null,
      razorpayMonthlyPrice: null,
      razorpayYearlyPrice: null,
      maxAgents: 1,
      maxCampaigns: 1,
      maxContactsPerCampaign: 100,
      maxWebhooks: 3,
      maxKnowledgeBases: 5,
      maxFlows: 3,
      maxPhoneNumbers: 0,
      phoneNumberPurchaseCredits: 25,
      includedCredits: 0,
      perMinuteRate: null,
      canChooseLlm: false,
      canPurchaseNumbers: false,
      useSystemPool: false,
    });
  };

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description,
      country: plan.country || "US",
      seatingEnabled: plan.seatingEnabled ?? false,
      maxSeats: plan.maxSeats || 1,
      monthlyPrice: parseFloat(plan.monthlyPrice),
      yearlyPrice: plan.yearlyPrice ? parseFloat(plan.yearlyPrice) : null,
      razorpayMonthlyPrice: plan.razorpayMonthlyPrice ? parseFloat(plan.razorpayMonthlyPrice) : null,
      razorpayYearlyPrice: plan.razorpayYearlyPrice ? parseFloat(plan.razorpayYearlyPrice) : null,
      maxAgents: plan.maxAgents,
      maxCampaigns: plan.maxCampaigns,
      maxContactsPerCampaign: plan.maxContactsPerCampaign,
      maxWebhooks: plan.maxWebhooks,
      maxKnowledgeBases: plan.maxKnowledgeBases,
      maxFlows: plan.maxFlows,
      maxPhoneNumbers: plan.maxPhoneNumbers,
      phoneNumberPurchaseCredits: plan.phoneNumberPurchaseCredits || 25,
      includedCredits: plan.includedCredits,
      perMinuteRate: plan.perMinuteRate ? parseFloat(plan.perMinuteRate) : null,
      canChooseLlm: plan.canChooseLlm,
      canPurchaseNumbers: plan.canPurchaseNumbers,
      useSystemPool: plan.useSystemPool,
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (plan: Plan) => {
    setDeletingPlan(plan);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (deletingPlan) {
      await deletePlan.mutateAsync(deletingPlan.id);
      setIsDeleteDialogOpen(false);
      setDeletingPlan(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if this is a free plan (free, trial, or free-trial)
    const isFreeOrTrialPlan = /^(free|trial|free[-_]trial)$/i.test(formData.name.toLowerCase());
    
    // Validate currency-specific prices
    const isIndia = formData.country === 'IN';
    
    if (isIndia) {
      // INR plan: require INR prices (razorpay prices) unless it's a free plan
      if (!isFreeOrTrialPlan && (!formData.razorpayMonthlyPrice || formData.razorpayMonthlyPrice <= 0)) {
        toast({
          title: 'Validation Error',
          description: 'Monthly price in INR is required for India plans',
          variant: 'destructive',
        });
        return;
      }
    } else {
      // USD plan: require USD prices unless it's a free plan
      if (!isFreeOrTrialPlan && (!formData.monthlyPrice || formData.monthlyPrice <= 0)) {
        toast({
          title: 'Validation Error',
          description: 'Monthly price in USD is required for US plans',
          variant: 'destructive',
        });
        return;
      }
    }
    
    if (editingPlan) {
      await updatePlan.mutateAsync({ id: editingPlan.id, data: formData });
    } else {
      await createPlan.mutateAsync(formData);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-40">
                  <Select
                    value={selectedCountry}
                    onValueChange={(value) => setSelectedCountry(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="IN">India</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription Plans
              </CardTitle>
              <CardDescription>
                Create and manage subscription plans for your platform
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Plan
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPlans.map((plan) => (
              <Card key={plan.id} className={`relative ${!plan.isActive ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{plan.displayName}</CardTitle>
                      <CardDescription className="mt-1">{plan.description}</CardDescription>
                    </div>
                  </div>
                  {(() => {
                    const isIndia = selectedCountry === "IN";
                    // For India: prefer razorpayMonthlyPrice, fallback to monthlyPrice
                    // For US: use monthlyPrice
                    const monthlyAmount = isIndia
                      ? (plan.razorpayMonthlyPrice ?? plan.monthlyPrice)
                      : plan.monthlyPrice;
                    const yearlyAmount = isIndia
                      ? (plan.razorpayYearlyPrice ?? plan.yearlyPrice)
                      : plan.yearlyPrice;
                    const currencySymbol = isIndia ? "₹" : "$";

                    return (
                      <div className="mt-4">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">
                            {monthlyAmount !== null && monthlyAmount !== undefined
                              ? `${currencySymbol}${parseFloat(monthlyAmount).toFixed(2)}`
                              : "—"}
                          </span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        {yearlyAmount !== null && yearlyAmount !== undefined && parseFloat(yearlyAmount) > 0 && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {currencySymbol}
                            {parseFloat(yearlyAmount).toFixed(2)}/year
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.includedCredits.toLocaleString()} credits (mins)</span>
                  </div>
                  {plan.perMinuteRate && (
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedCountry === "IN" ? "₹" : "$"}{parseFloat(plan.perMinuteRate).toFixed(2)}/min</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.maxAgents} agents</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.maxPhoneNumbers} phone numbers ({plan.phoneNumberPurchaseCredits || 25} credits/phone)</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span>{plan.maxCampaigns} campaigns</span>
                  </div>

                  {/* Plan Permissions Badges */}
                  <div className="flex flex-wrap gap-1 pt-2">
                    {plan.canPurchaseNumbers && (
                      <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                        Can Purchase Numbers
                      </span>
                    )}
                    {plan.useSystemPool && (
                      <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700">
                        System Pool
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(plan)}
                      className="flex-1"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(plan)}
                      className="flex-1 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredPlans.length === 0 && (
            <Alert>
              <AlertDescription className="text-center py-8">
                No subscription plans found. Create your first plan to get started.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          setEditingPlan(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
            <DialogDescription>
              {editingPlan ? 'Update the subscription plan details' : 'Configure a new subscription plan for your platform'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold">Basic Information</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Plan Name (Internal)</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., starter, growth, scale"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Lowercase, no spaces (used in code)
                  </p>
                </div>
                
                <div>
                  <Label>Display Name</Label>
                  <Input
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="e.g., Starter Plan"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the plan"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Country</Label>
                  <Select 
                    value={formData.country} 
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="IN">India</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Seat-Based Plan</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.seatingEnabled}
                      onChange={(e) =>
                        setFormData({ ...formData, seatingEnabled: e.target.checked })
                      }
                    />
                    <span className="text-sm">Enable multi-seat usage</span>
                  </div>

                  {formData.seatingEnabled && (
                    <>
                      <Label className="mt-2 block">Number of Seats</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.maxSeats}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || /^\d+$/.test(value)) {
                            setFormData({
                              ...formData,
                              maxSeats: parseInt(value, 10) || 1,
                            });
                          }
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum number of seats allowed
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Pricing</h4>
                <div className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700">
                  {formData.country === 'IN' ? '₹ INR Plan' : '$ USD Plan'}
                </div>
              </div>
              
              {formData.country === 'US' && (
                <div className="p-3 rounded bg-blue-50 border border-blue-200 text-sm text-blue-900">
                  <strong>USD Plan:</strong> Enter prices in US Dollars. These will be synced to Stripe.
                </div>
              )}
              
              {formData.country === 'IN' && (
                <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                  <strong>INR Plan:</strong> Enter prices in Indian Rupees. These will be synced to Razorpay.
                </div>
              )}
              
              {formData.country !== 'IN' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Monthly Price ($) <span className="text-red-500">*</span></Label>
                      <InfoTooltip content="Monthly subscription price in USD" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monthlyPrice}
                      onChange={(e) => setFormData({ ...formData, monthlyPrice: parseFloat(e.target.value) || 0 })}
                      placeholder="Required for US plans"
                      className={formData.monthlyPrice <= 0 ? 'border-red-500' : ''}
                    />
                    {formData.monthlyPrice <= 0 && (
                      <p className="text-xs text-red-500 mt-1">Required for US plans</p>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Yearly Price ($)</Label>
                      <InfoTooltip content="Optional yearly subscription price (leave 0 for monthly only)" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.yearlyPrice || 0}
                      onChange={(e) => setFormData({ ...formData, yearlyPrice: parseFloat(e.target.value) || null })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}

              {formData.country === 'IN' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Monthly Price (₹ INR) <span className="text-red-500">*</span></Label>
                      <InfoTooltip content="Monthly subscription price in Indian Rupees (Razorpay)" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.razorpayMonthlyPrice || 0}
                      onChange={(e) => setFormData({ ...formData, razorpayMonthlyPrice: parseFloat(e.target.value) || null })}
                      placeholder="Required for India plans"
                      className={(formData.razorpayMonthlyPrice === null || formData.razorpayMonthlyPrice <= 0) ? 'border-red-500' : ''}
                    />
                    {(formData.razorpayMonthlyPrice === null || formData.razorpayMonthlyPrice <= 0) && (
                      <p className="text-xs text-red-500 mt-1">Required for India plans</p>
                    )}
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2">
                      <Label>Yearly Price (₹ INR)</Label>
                      <InfoTooltip content="Optional yearly subscription price in Indian Rupees" />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.razorpayYearlyPrice || 0}
                      onChange={(e) => setFormData({ ...formData, razorpayYearlyPrice: parseFloat(e.target.value) || null })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              )}

              {/* Per Minute Rate */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Per Minute Rate ({formData.country === 'IN' ? '₹' : '$'})</Label>
                    <InfoTooltip content="Cost per minute for credit calculation. If set, credits = plan amount / rate" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.perMinuteRate || ''}
                    onChange={(e) => {
                      const rate = parseFloat(e.target.value) || null;
                      setFormData({ ...formData, perMinuteRate: rate });
                    }}
                    placeholder="e.g., 5.50"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty to set credits manually
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <Label>Included Credits (Minutes)</Label>
                    <InfoTooltip content="Number of voice call minutes included per month (1 credit = 1 minute)" />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={formData.includedCredits}
                    onChange={(e) => setFormData({ ...formData, includedCredits: parseInt(e.target.value, 10) || 0 })}
                    required
                  />
                  {formData.perMinuteRate && formData.perMinuteRate > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      Auto-calculated: {(() => {
                        const price = formData.country === 'IN'
                          ? (formData.razorpayMonthlyPrice || 0)
                          : (formData.monthlyPrice || 0);
                        const calculated = Math.floor(price / formData.perMinuteRate);
                        return `${price.toLocaleString()} / ${formData.perMinuteRate} = ${calculated.toLocaleString()} mins`;
                      })()}
                      <button
                        type="button"
                        className="ml-2 text-blue-700 underline"
                        onClick={() => {
                          const price = formData.country === 'IN'
                            ? (formData.razorpayMonthlyPrice || 0)
                            : (formData.monthlyPrice || 0);
                          const calculated = Math.floor(price / (formData.perMinuteRate || 1));
                          setFormData({ ...formData, includedCredits: calculated });
                        }}
                      >
                        Apply
                      </button>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Limits */}
            <div className="space-y-4">
              <h4 className="font-semibold">Resource Limits</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Max Agents</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.maxAgents}
                    onChange={(e) => setFormData({ ...formData, maxAgents: parseInt(e.target.value, 10) || 1 })}
                    required
                  />
                </div>
                
                <div>
                  <Label>Max Phone Numbers</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxPhoneNumbers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      setFormData({ ...formData, maxPhoneNumbers: isNaN(value) ? 0 : value });
                    }}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Set to 0 for no phone number access
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <Label>Phone Purchase Credits</Label>
                    <InfoTooltip content="Number of credits deducted when user purchases/rents a phone number" />
                  </div>
                  <Input
                    type="number"
                    min="0"
                    value={formData.phoneNumberPurchaseCredits}
                    onChange={(e) => setFormData({ ...formData, phoneNumberPurchaseCredits: parseInt(e.target.value, 10) || 25 })}
                    placeholder="e.g., 25, 40, 50"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Credits deducted per phone number purchase
                  </p>
                </div>

                <div>
                  <Label>Max Campaigns</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.maxCampaigns}
                    onChange={(e) => setFormData({ ...formData, maxCampaigns: parseInt(e.target.value, 10) || 1 })}
                    required
                  />
                </div>

                <div>
                  <Label>Max Contacts Per Campaign</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.maxContactsPerCampaign}
                    onChange={(e) => setFormData({ ...formData, maxContactsPerCampaign: parseInt(e.target.value, 10) || 100 })}
                    required
                  />
                </div>

                <div>
                  <Label>Max Webhooks</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxWebhooks}
                    onChange={(e) => setFormData({ ...formData, maxWebhooks: parseInt(e.target.value, 10) || 3 })}
                    required
                  />
                </div>

                <div>
                  <Label>Max Knowledge Bases</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxKnowledgeBases}
                    onChange={(e) => setFormData({ ...formData, maxKnowledgeBases: parseInt(e.target.value, 10) || 5 })}
                    required
                  />
                </div>

                <div>
                  <Label>Max Flows</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.maxFlows}
                    onChange={(e) => setFormData({ ...formData, maxFlows: parseInt(e.target.value, 10) || 3 })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Plan Permissions */}
            <div className="space-y-4">
              <h4 className="font-semibold">Plan Permissions</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 p-3 rounded border">
                  <input
                    type="checkbox"
                    id="canPurchaseNumbers"
                    checked={formData.canPurchaseNumbers}
                    onChange={(e) => setFormData({ ...formData, canPurchaseNumbers: e.target.checked })}
                  />
                  <div>
                    <Label htmlFor="canPurchaseNumbers" className="cursor-pointer">Can Purchase Phone Numbers</Label>
                    <p className="text-xs text-muted-foreground">Allow users to buy/rent phone numbers</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 rounded border">
                  <input
                    type="checkbox"
                    id="useSystemPool"
                    checked={formData.useSystemPool}
                    onChange={(e) => setFormData({ ...formData, useSystemPool: e.target.checked })}
                  />
                  <div>
                    <Label htmlFor="useSystemPool" className="cursor-pointer">Use System Phone Pool</Label>
                    <p className="text-xs text-muted-foreground">Use shared system numbers instead of dedicated</p>
                  </div>
                </div>

              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                  setEditingPlan(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createPlan.isPending || updatePlan.isPending}>
                {(createPlan.isPending || updatePlan.isPending) ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {editingPlan ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingPlan ? 'Update Plan' : 'Create Plan'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the &quot;{deletingPlan?.displayName}&quot; plan? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deletePlan.isPending}>
              {deletePlan.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
